/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';

export const ModelGuidanceCardMessages = defineMessages({
    guidanceTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.guidanceTitle',
        defaultMessage: 'Guidance'
    },
    documents: {
        id: 'modelGuidance.modelGuidanceCardMessages.documents',
        defaultMessage: 'documents'
    },
    utterances: {
        id: 'modelGuidance.modelGuidanceCardMessages.utterances',
        defaultMessage: 'utterances'
    },
    guidanceSubtitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.guidanceSubtitle',
        defaultMessage: 'To improve your model’s performance, review these issues and follow any recommendations.'
    },
    notEnoughDataError: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughDataError',
        defaultMessage: 'Not enough data in training set.'
    },
    notEnoughDataErrorDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughDataErrorDescription',
        defaultMessage:
            'When {nonSpecificLabelType} has fewer than 15 labeled instances in the training data, it can lead to lower accuracy due to the model not being adequately trained on these cases.'
    },
    notEnoughDataRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughDataRight',
        defaultMessage: 'Training set has enough data.'
    },
    notEnoughDataIssuesNumberMessage: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughDataIssuesNumberMessage',
        defaultMessage: '{issuesNumber} {labelType} with low data.'
    },
    notEnoughDataRecommendationTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughDataRecommendationTitle',
        defaultMessage: '{labelType} with low data in training set'
    },
    notEnoughDataRecommendationDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughDataRecommendationDescription',
        defaultMessage: 'Add {type} that are labeled with the following {labelType} to the training set.'
    },
    missingLabelsError: {
        id: 'modelGuidance.modelGuidanceCardMessages.missingLabelsError',
        defaultMessage: 'Missing {labelType} in test set.'
    },
    missingLabelsErrorDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.missingLabelsErrorDescription',
        defaultMessage:
            'When the testing data lacks labeled instances for {nonSpecificLabelType}, the model’s test performance may become less comprehensive due to untested scenarios.'
    },
    missingLabelsRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.missingLabelsRight',
        defaultMessage: 'All {labelType} are present in test set.'
    },
    missingLabelsIssuesNumberMessage: {
        id: 'modelGuidance.modelGuidanceCardMessages.missingLabelsIssuesNumberMessage',
        defaultMessage: '{issuesNumber} missing {labelType}.'
    },
    missingLabelRecommendationTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.missingLabelRecommendationTitle',
        defaultMessage: '{labelType} that are missing from test set.'
    },
    missingLabelRecommendationDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.missingLabelRecommendationDescription',
        defaultMessage: 'Add {type} that are labeled with the missing {labelType} to the test set.'
    },
    unbalancedLabelsError: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsError',
        defaultMessage: 'Unbalanced {labelType} within training or test set.'
    },
    unbalancedLabelsShortError: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsShortError',
        defaultMessage: 'Unbalanced {labelType}.'
    },
    unbalancedLabelsErrorDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsErrorDescription',
        defaultMessage:
            'When sampling bias causes an inaccurate representation of {nonSpecificLabelType} frequency, it can lead to lower accuracy due to the model expecting that {labelType} to occur too often or too little.'
    },
    unbalancedLabelsRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsRight',
        defaultMessage: '{labelType} are balanced within training and test sets.'
    },
    unbalancedLabelsShortRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsShortRight',
        defaultMessage: '{labelType} are balanced.'
    },
    unbalancedLabelsIssuesNumberMessage: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsIssuesNumberMessage',
        defaultMessage: '{issuesNumber} unbalanced {labelType}.'
    },
    unbalancedLabelsRecommendationTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsRecommendationTitle',
        defaultMessage: 'Underrepresented {labelType} that are causing their dataset to be unbalanced.'
    },
    unbalancedLabelsRecommendationDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.unbalancedLabelsRecommendationDescription',
        defaultMessage: 'Add {type} that are labeled with the underrepresented {labelType} to the affected dataset.'
    },
    unEvenDistributionError: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionError',
        defaultMessage: 'Uneven distribution of {labelType} between training and test sets.'
    },
    unEvenDistributionShortError: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionShortError',
        defaultMessage: 'Uneven distribution of {labelType}.'
    },
    unEvenDistributionErrorDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionErrorDescription',
        defaultMessage:
            'When the mix of {labelType} doesn’t match between training and test sets, it can lead to lower testing accuracy due to the model being trained differently from how it’s being tested.'
    },
    unEvenDistributionRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionRight',
        defaultMessage: '{labelType} are evenly distributed between training and test sets.'
    },
    unEvenDistributionShortRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionShortRight',
        defaultMessage: '{labelType} are evenly distributed.'
    },
    unEvenDistributionIssuesNumberMessage: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionIssuesNumberMessage',
        defaultMessage: '{labelType} distributed unevenly.'
    },
    unEvenDistributionRecommendationTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionRecommendationTitle',
        defaultMessage: 'Datasets with uneven distributions of {labelType}'
    },
    unEvenDistributionRecommendationDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.unEvenDistributionRecommendationDescription',
        defaultMessage:
            'Determine which {labelType} are represented at different ratios between the training and test sets. Add {type} that are labeled with underrepresented {labelType} to the relevant dataset.'
    },
    unClearDistinctionError: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionError',
        defaultMessage: 'Unclear distinction between {labelType} in training set.'
    },
    unClearDistinctionShortError: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionShortError',
        defaultMessage: 'Unclear distinction between {labelType}.'
    },
    unClearDistinctionErrorDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionErrorDescription',
        defaultMessage:
            'When the training data is similar for multiple {labelType}, it can lead to lower accuracy because the {labelType} may be frequently misclassified as each other.'
    },
    unClearDistinctionRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionRight',
        defaultMessage: '{labelType} in training set are clearly distinct.'
    },
    unClearDistinctionShortRight: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionShortRight',
        defaultMessage: '{labelType} are clearly distinct.'
    },
    unClearDistinctionIssuesNumberMessage: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionIssuesNumberMessage',
        defaultMessage: '{issuesNumber} similar {labelType}.'
    },
    unClearDistinctionRecommendationTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionRecommendationTitle',
        defaultMessage: '{labelType} with an unclear distinction in training set.'
    },
    unClearDistinctionRecommendationDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.unClearDistinctionRecommendationDescription',
        defaultMessage:
            'Review the following {labelType} and consider merging them if they’re similar. Otherwise, add more examples to better distinguish them from each other.'
    },
    confusionMatrix: {
        id: 'modelGuidance.modelGuidanceCardMessages.confusionMatrix',
        defaultMessage: 'confusion matrix'
    },
    dataDistributionLink: {
        id: 'modelGuidance.modelGuidanceCardMessages.dataDistributionLink',
        defaultMessage: 'data distribution'
    },
    testSetDataDistributionLink: {
        id: 'modelGuidance.modelGuidanceCardMessages.testSetDataDistributionLink',
        defaultMessage: 'test set data distribution'
    },
    trainingSetDataDistributionLink: {
        id: 'modelGuidance.modelGuidanceCardMessages.trainingSetDataDistributionLink',
        defaultMessage: 'training set data distribution'
    },
    check: {
        id: 'modelGuidance.modelGuidanceCardMessages.check',
        defaultMessage: 'Check the '
    },
    notEnoughLabelsInTrainingSet: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughLabelsInTrainingSet',
        defaultMessage: 'Not enough {labelType} labels in training set'
    },
    notEnoughEntitiesInTrainingSetRecommendation: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughLabelsInTrainingSetRecommendation',
        defaultMessage: `We recommend a minimum of 15 labels per entity in the training data when defining a learned component.
         Complex entities may require a lot more labels for accurate predictions. Alternatively, add a list, regex, or prebuilt component to the entity if applicable. `
    },
    notEnoughIntentsInTrainingSetRecommendation: {
        id: 'modelGuidance.modelGuidanceCardMessages.notEnoughIntentsInTrainingSetRecommendation',
        defaultMessage: 'We recommend a minimum of 15 utterances per intent in your training data.'
    },
    forDetails: {
        id: 'modelGuidance.modelGuidanceCardMessages.forDetails',
        defaultMessage: ' for details.'
    },
    recommendation: {
        id: 'modelGuidance.modelGuidanceCardMessages.recommendation',
        defaultMessage: 'Recommendation'
    },
    improveModelTitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelTitle',
        defaultMessage: 'Improving your model'
    },
    improveModelSubtitle: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelSubtitle',
        defaultMessage: 'Increase the quality of your model by taking the following actions.'
    },
    improveModelFirstActionLinkText: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelFirstActionLinkText',
        defaultMessage: 'Review the test set predictions.'
    },
    improveModelFirstActionDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelFirstActionDescription',
        defaultMessage:
            ' After training, the model may make predictions that conflict with your labeled data. This can be due to labeling mistakes or lack of data. To correct a prediction, change the label in the test set or add more examples, then train the model again.'
    },
    improveModelSecondActionLinkText: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelSecondActionLinkText',
        defaultMessage: 'Check the data distribution.'
    },
    improveModelSecondActionDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelSecondActionDescription',
        defaultMessage:
            ' If labels for each {labelType} are unevenly distributed, it can lead to the model making incorrect predictions. To correct an uneven data distribution, add more examples for any underrepresented {pluralLabelType}, then train the model again.'
    },
    improveModelThirdActionLinkText: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelThirdActionLinkText',
        defaultMessage: 'Review the labeled {type}.'
    },
    improveModelThirdActionDescription: {
        id: 'modelGuidance.modelGuidanceCardMessages.improveModelThirdActionDescription',
        defaultMessage: ' Correct any errors in your labeled training and test data, then train the model again.'
    },
    inTrainingSet: {
        id: 'modelGuidance.modelGuidanceCardMessages.inTrainingSet',
        defaultMessage: 'in training set.'
    },
    inTestSet: {
        id: 'modelGuidance.modelGuidanceCardMessages.inTestSet',
        defaultMessage: 'in test set.'
    },
    mayBeSimilar: {
        id: 'modelGuidance.modelGuidanceCardMessages.mayBeSimilar',
        defaultMessage: 'may be similar to'
    },
    trainingSet: {
        id: 'modelGuidance.modelGuidanceCardMessages.trainingSet',
        defaultMessage: 'Training set'
    },
    testSet: {
        id: 'modelGuidance.modelGuidanceCardMessages.testSet',
        defaultMessage: 'Test set'
    },
    noRecommendationAvailable1: {
        id: 'modelGuidance.modelGuidanceCardMessages.noRecommendationAvailable1',
        defaultMessage:
            'We have improved our evaluation results to show you specified recommended guidance based on your model. You will need to'
    },
    noRecommendationAvailable2: {
        id: 'modelGuidance.modelGuidanceCardMessages.noRecommendationAvailable2',
        defaultMessage: 'your model to view these recommendations.'
    },
    retrain: {
        id: 'modelGuidance.modelGuidanceCardMessages.retrain',
        defaultMessage: 'retrain'
    },
    distributionEmptyState1: {
        id: 'modelGuidance.modelGuidanceCardMessages.distributionEmptyState1',
        defaultMessage:
            'We have improved our evaluation results to show you the snapshot of your data distribution for this model. You will need to'
    },
    distributionEmptyState2: {
        id: 'modelGuidance.modelGuidanceCardMessages.distributionEmptyState2',
        defaultMessage: `to view the model's data distribution snapshot.`
    }
});
